import React from 'react';
import cn from 'classnames';
import styles from './styles.module.css';
import LoadingSpinner from '../LoadingSpinner';
import { withNestedLink } from '../nestedLinkBox/NestedLinkBox';

export type RowData = {
  key: string | number;
  onClick?: () => void;
  linkTo?: string;
  values: React.ReactNode[];
};

export type DirectionType = 'asc' | 'desc';

export type SortInfo = {
  direction: DirectionType;
  sortedFieldValue: string;
};

export type ColumnOption = {
  header: string;
  headerValue?: string;
  canSort?: boolean;
};

export type TableProps = {
  headers: ColumnOption[];
  data?: RowData[];
  noRowsValue?: React.ReactNode;
  message?: React.ReactNode;
  loading?: boolean;
  multiLine?: boolean;
  verticallyCenterCellContent?: boolean;
  sortInfo?: SortInfo;
  onSort?: (headerValue: string) => void;
};

const Table: React.FC<TableProps> = ({
  headers,
  data,
  noRowsValue,
  message,
  loading,
  multiLine,
  verticallyCenterCellContent = false,
  sortInfo,
  onSort,
}) => {
  const effectiveValign = verticallyCenterCellContent
    ? styles.cellValignMiddle
    : styles.cellValignTop;

  const renderMessage = (val: React.ReactNode) => (
    <tr key="helperMessage">
      <td
        className={cn(styles.noRowsValue, effectiveValign)}
        colSpan={headers.length}
      >
        {val}
      </td>
    </tr>
  );

  const renderLoadingSpinner = () => (
    <tr>
      <td
        className={cn(styles.loading, effectiveValign)}
        colSpan={headers.length}
      >
        Loading... <LoadingSpinner />
      </td>
    </tr>
  );

  let body;

  if (loading) {
    body = renderLoadingSpinner();
  } else if (data && data.length) {
    body = data.map(({ key, values, onClick, linkTo }) => (
      <Row
        key={key}
        data={values}
        onClick={onClick}
        linkTo={linkTo}
        multiLine={multiLine}
        effectiveValign={effectiveValign}
      />
    ));
    if (message) {
      body.push(renderMessage(message));
    }
  } else if (message) {
    body = renderMessage(message);
  } else if (!data?.length) {
    body = renderMessage(noRowsValue);
  } else {
    body = renderLoadingSpinner();
  }

  return (
    <table
      className={cn(styles.tableStriped, styles.table, 'search-results-table')}
    >
      <thead className={cn(styles.tableHead)}>
        <tr>
          {headers.map(header => {
            return (
              <th
                className={cn(
                  styles.header,
                  header.canSort &&
                    sortInfo?.sortedFieldValue === header.headerValue
                    ? 'sort'
                    : header.canSort ?? 'no-sort',
                )}
                key={header.header}
                onClick={() =>
                  header.canSort &&
                  header.headerValue &&
                  data &&
                  onSort != undefined
                    ? onSort(header.headerValue)
                    : undefined
                }
              >
                {header.header}
                {sortInfo?.sortedFieldValue === header.headerValue && (
                <i
                  className={`sprites ${
                    sortInfo?.direction === 'asc'
                      ? 'sprite-arrow-orange-up'
                      : 'sprite-arrow-orange-down'
                  }`}
                ></i>
              )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className={cn(styles.tbody)}>{body}</tbody>
    </table>
  );
};

type RowProps = {
  data: React.ReactNode[],
  onClick?: () => void,
  linkTo?: string,
  multiLine?: boolean,
  effectiveValign?: string,
};

const Row: React.FC<RowProps> = ({
  data,
  onClick,
  linkTo,
  multiLine,
  effectiveValign = styles.cellValignTop,
}) => {
  const rowClasses = [];
  if (onClick || linkTo) {
    rowClasses.push(styles.clickable);
  }

  return (
    <tr className={cn(...rowClasses)} {...{ onClick }}>
      {// eslint-disable-next-line react/no-array-index-key
      data.map((elem, idx) => (
        <Data
          data={elem}
          key={idx}
          linkTo={linkTo}
          multiLine={multiLine}
          effectiveValign={effectiveValign}
        />
      ))}
    </tr>
  );
};

type DataProps = {
  data: React.ReactNode;
  linkTo?: string;
  multiLine?: boolean;
  effectiveValign?: string;
};

const Data: React.FC<DataProps> = ({
  data,
  linkTo,
  multiLine,
  effectiveValign = styles.cellValignTop,
}) => {
  const cell = (
    <td className={cn(effectiveValign)}>
      {multiLine && Array.isArray(data)
        ? data.map((item, count) => <div key={count}>{item}</div>)
        : data}
    </td>
  );

  if (linkTo) {
    return withNestedLink(linkTo, cell);
  }

  return cell;
};

export default Table;
